import React, { useRef, useState } from "react";
import {
  Alert, AlertTitle, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, IconButton,
  ListItemIcon, ListItemText, Menu, MenuItem, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  DiscoverableDocument, FiscalDocument, useApplyFiscalDocument, useDeleteFiscalDocument,
  useDiscoverableDocuments, useFiscalDocument, useFiscalDocuments, useImportExistingDocuments,
  useRejectFiscalDocument, useReplaceFiscalDocumentFile, useUpdateFiscalDocument,
  useUploadFiscalDocument,
} from "../../../api/fiscalite";
import { usePlateResolver } from "../../../api/vehicleOptions";
import { VehiclePicker } from "../../../components/VehiclePicker/VehiclePicker";

const DOC_TYPES = [
  { key: "GREY_CARD", label: "Carte grise" },
  { key: "PURCHASE_INVOICE", label: "Facture d'achat vehicule" },
  { key: "LEASE_CONTRACT", label: "Contrat LLD / LOA" },
  { key: "EXPENSE_INVOICE", label: "Facture de charge" },
];

const STATUS_COLOR: Record<string, "default" | "info" | "warning" | "success" | "error"> = {
  UPLOADED: "info",
  EXTRACTING: "info",
  NEEDS_REVIEW: "warning",
  APPLIED: "success",
  REJECTED: "default",
  FAILED: "error",
};

interface Props {
  fleetId: number;
}

/**
 * APPLIED is the only state the correction actions are refused on, and the
 * refusal is the server's: PATCH, replace and DELETE all answer 409 for it.
 *
 * Mirrored here rather than discovered by pressing a button, because the reason
 * deserves to be said before the fact — the document is the piece justificative
 * behind a figure already written to a fiscal profile or the cost ledger, and
 * the correction path is that profile, not this table.
 */
const isLocked = (status: FiscalDocument["status"]) => status === "APPLIED";

/**
 * Server messages on this resource are written for the end user (the 409 says
 * what to do instead), so they are shown as-is when present. The fallback is
 * only for a transport failure, where there is no server message to show.
 */
const serverMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: { message?: string } } })?.response?.data;
  return data?.message || fallback;
};

/**
 * Document intake and review.
 *
 * The review dialog is the heart of the feature, and its design is deliberate:
 * the extractor's output lands in EDITABLE fields, never in read-only text.
 * OCR on a photographed registration certificate is an accelerator, not an
 * authority — a misread CO2 value would flow straight into a tax figure and
 * stay invisible until an audit. Nothing is written until the user confirms.
 */
export const Documents: React.FC<Props> = ({ fleetId }) => {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);

  const [docType, setDocType] = useState("GREY_CARD");
  // The vehicle chosen in the UPLOAD form. Deliberately separate from
  // reviewVehicleId below: they are two different questions asked at two
  // different moments, and conflating them applied values to the wrong car.
  const [vehicleId, setVehicleId] = useState<number | null>(null);
  const [reviewingId, setReviewingId] = useState<number | null>(null);
  const [reviewVehicleId, setReviewVehicleId] = useState<number | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});

  // Correction flow. `menuDocument` holds the whole row rather than an id so
  // the menu can gate its own items on the document's status without a lookup.
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuDocument, setMenuDocument] = useState<FiscalDocument | null>(null);
  const [editing, setEditing] = useState<FiscalDocument | null>(null);
  const [editType, setEditType] = useState("GREY_CARD");
  const [editVehicleId, setEditVehicleId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<FiscalDocument | null>(null);
  // One input reused by every row: the target is whatever `replaceTargetId`
  // holds when the picker opens.
  const replaceInput = useRef<HTMLInputElement>(null);
  const [replaceTargetId, setReplaceTargetId] = useState<number | null>(null);

  const { documentsPayload, isLoading } = useFiscalDocuments(fleetId);
  const { documentPayload } = useFiscalDocument(fleetId, reviewingId ?? undefined);
  const upload = useUploadFiscalDocument(fleetId);
  const apply = useApplyFiscalDocument(fleetId);
  const reject = useRejectFiscalDocument(fleetId);
  const update = useUpdateFiscalDocument(fleetId);
  const replaceFile = useReplaceFiscalDocumentFile(fleetId);
  const remove = useDeleteFiscalDocument(fleetId);
  const plateOf = usePlateResolver(fleetId);
  const documents: FiscalDocument[] = documentsPayload?.documents?.data ?? [];
  const pendingReview = documentsPayload?.pending_review ?? 0;

  const onPick = async (file?: File | null) => {
    if (!file) return;
    await upload.mutateAsync({
      file,
      doc_type: docType,
      vehicle_id: vehicleId ?? undefined,
    });
    if (fileInput.current) fileInput.current.value = "";
  };

  const openReview = (document: FiscalDocument) => {
    setReviewingId(document.id);
    // The vehicle under review is the one the DOCUMENT is attached to.
    setReviewVehicleId(document.vehicle_id ?? null);
    // Pre-fill from the extraction, then let the user correct anything.
    const extracted = (document.extracted?.values ?? {}) as Record<string, unknown>;
    const initial: Record<string, string> = {};
    Object.entries(extracted).forEach(([key, value]) => {
      initial[key] = value === null || value === undefined ? "" : String(value);
    });
    setValues(initial);
  };

  const confirm = async () => {
    if (!reviewingId) return;
    const cleaned = Object.fromEntries(
      Object.entries(values).filter(([, value]) => value !== "")
    );
    // `reviewVehicleId`, never the upload form's `vehicleId`. This used to send
    // the upload picker's value, so validating a grey card while the picker
    // still pointed at the last vehicle uploaded wrote that car's CO2 onto a
    // different one — a wrong tax figure on two vehicles at once, with the
    // document filed as the justification for it.
    await apply.mutateAsync({
      documentId: reviewingId,
      vehicle_id: reviewVehicleId ?? undefined,
      values: cleaned,
    });
    setReviewingId(null);
  };

  const editableFields: string[] = documentPayload?.editable_fields ?? Object.keys(values);

  // ── Correction handlers ────────────────────────────────────────────────

  const openMenu = (anchor: HTMLElement, document: FiscalDocument) => {
    setMenuAnchor(anchor);
    setMenuDocument(document);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuDocument(null);
  };

  const openEdit = (document: FiscalDocument) => {
    setEditing(document);
    setEditType(document.doc_type);
    setEditVehicleId(document.vehicle_id ?? null);
    closeMenu();
  };

  const submitEdit = () => {
    if (!editing) return;

    const typeChanged = editType !== editing.doc_type;
    const vehicleChanged = (editVehicleId ?? null) !== (editing.vehicle_id ?? null);

    if (!typeChanged && !vehicleChanged) {
      setEditing(null);
      return;
    }

    update.mutate(
      {
        documentId: editing.id,
        // Only what actually changed. Resending an identical doc_type would
        // be harmless server-side, but sending it as "changed" is what would
        // trigger a pointless re-extraction.
        ...(typeChanged ? { doc_type: editType } : {}),
        ...(vehicleChanged ? { vehicle_id: editVehicleId } : {}),
      },
      {
        onSuccess: (data) => {
          setEditing(null);
          toast.success(data.message);
        },
        onError: (error) =>
          toast.error(serverMessage(error, t("tax.docUpdateFailed", "Modification impossible."))),
      }
    );
  };

  const startReplace = (document: FiscalDocument) => {
    setReplaceTargetId(document.id);
    closeMenu();
    replaceInput.current?.click();
  };

  const onReplacePicked = (file?: File | null) => {
    const documentId = replaceTargetId;
    // Cleared before the request so a cancelled picker cannot leave a stale
    // target pointing at a row the user has since moved on from.
    setReplaceTargetId(null);
    if (replaceInput.current) replaceInput.current.value = "";
    if (!file || !documentId) return;

    replaceFile.mutate(
      { documentId, file },
      {
        onSuccess: () =>
          toast.success(
            t("tax.docReplaced", "Fichier remplace. L'extraction est relancee.")
          ),
        onError: (error) =>
          toast.error(serverMessage(error, t("tax.docReplaceFailed", "Remplacement impossible."))),
      }
    );
  };

  const confirmDelete = () => {
    if (!deleting) return;

    remove.mutate(deleting.id, {
      onSuccess: () => {
        setDeleting(null);
        toast.success(t("tax.docDeleted", "Document supprime."));
      },
      onError: (error) => {
        setDeleting(null);
        toast.error(serverMessage(error, t("tax.docDeleteFailed", "Suppression impossible.")));
      },
    });
  };

  // Only the batch target (fuel-card statements) can be applied without a
  // vehicle; everything else writes to a fiscal profile or a ledger line that
  // must name one. Mirrors FiscalDocumentController::apply().
  const needsVehicle = (documentPayload?.target ?? null) !== "cost_entry_batch";

  return (
    <Stack spacing={3}>
      {/* Before asking for an upload, offer what the customer has already
          given us. An established fleet usually has its cartes grises in the
          coffre-fort, and the module used to ignore them entirely and show an
          empty completeness screen instead. */}
      <ReprisePanel fleetId={fleetId} />

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t("tax.uploadTitle", "Importer un document")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(
              "tax.uploadHint",
              "Carte grise, facture ou contrat de location. Les valeurs sont extraites automatiquement puis soumises a votre validation."
            )}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <TextField
              select size="small" label={t("tax.docType", "Type")}
              value={docType} onChange={(e) => setDocType(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              {DOC_TYPES.map((type) => (
                <MenuItem key={type.key} value={type.key}>{type.label}</MenuItem>
              ))}
            </TextField>

            <VehiclePicker
              fleetId={fleetId}
              value={vehicleId}
              onChange={setVehicleId}
              label={t("tax.vehicleOptional", "Vehicule (optionnel)")}
              sx={{ minWidth: 260 }}
            />

            <input
              ref={fileInput} type="file" hidden
              accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
              onChange={(e) => onPick(e.target.files?.[0])}
            />
            <Button variant="contained" onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
              {upload.isPending ? t("common.loading", "Envoi...") : t("tax.chooseFile", "Choisir un fichier")}
            </Button>
          </Stack>

          {upload.data?.duplicate && (
            <Alert severity="info" sx={{ mt: 2 }}>{upload.data.message}</Alert>
          )}
        </CardContent>
      </Card>

      {pendingReview > 0 && (
        <Alert severity="warning">
          {t("tax.pendingReview", {
            defaultValue: "{{count}} document(s) attendent votre validation.",
            count: pendingReview,
          })}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t("tax.documents", "Documents importes")}
          </Typography>

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("tax.file", "Fichier")}</TableCell>
                  <TableCell>{t("tax.docType", "Type")}</TableCell>
                  <TableCell>{t("tax.vehicle", "Vehicule")}</TableCell>
                  <TableCell>{t("common.status", "Statut")}</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((document) => (
                  <TableRow key={document.id} hover>
                    <TableCell>{document.original_name ?? `#${document.id}`}</TableCell>
                    <TableCell>
                      {DOC_TYPES.find((type) => type.key === document.doc_type)?.label ?? document.doc_type}
                    </TableCell>
                    <TableCell>{document.vehicle_id ? plateOf(document.vehicle_id) : "-"}</TableCell>
                    <TableCell>
                      <Chip size="small" color={STATUS_COLOR[document.status] ?? "default"} label={document.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                        {document.status === "NEEDS_REVIEW" && (
                          <Button size="small" variant="contained" onClick={() => openReview(document)}>
                            {t("tax.review", "Verifier")}
                          </Button>
                        )}
                        {document.status === "FAILED" && (
                          <Typography variant="caption" color="error">
                            {t("tax.extractionFailed", "Extraction impossible - saisie manuelle")}
                          </Typography>
                        )}

                        {/* Disabled with the reason attached rather than
                            hidden: a missing button reads as a bug, whereas a
                            disabled one that explains itself teaches why an
                            applied document is final. The <span> is required —
                            MUI cannot hang a tooltip off a disabled button. */}
                        <Tooltip
                          title={
                            isLocked(document.status)
                              ? t(
                                  "tax.docLockedApplied",
                                  "Document applique : il justifie une valeur deja inscrite sur la fiche fiscale. Corrigez la fiche du vehicule."
                                )
                              : t("common.actions", "Actions")
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={isLocked(document.status)}
                              onClick={(event) => openMenu(event.currentTarget, document)}
                              aria-label={t("common.actions", "Actions")}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!reviewingId} onClose={() => setReviewingId(null)} maxWidth="md" fullWidth>
        <DialogTitle>{t("tax.reviewTitle", "Verifier les valeurs extraites")}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {documentPayload?.note
              ?? t(
                "tax.reviewNote",
                "Verifiez chaque valeur sur le document avant de valider. Rien n'est enregistre tant que vous n'avez pas confirme."
              )}
          </Alert>

          {documentPayload?.preview_url && (
            <Button size="small" href={documentPayload.preview_url} target="_blank" rel="noreferrer" sx={{ mb: 2 }}>
              {t("tax.openDocument", "Ouvrir le document")}
            </Button>
          )}

          {/* Documents are routinely uploaded before anyone knows which vehicle
              they belong to, and the server refuses to apply a grey card or an
              invoice without one. Without this picker the review dialog had no
              way to supply it and those documents were simply unappliable. */}
          <VehiclePicker
            fleetId={fleetId}
            value={reviewVehicleId}
            onChange={setReviewVehicleId}
            label={t("tax.vehicle", "Vehicule")}
            sx={{ mb: 2, minWidth: 260 }}
          />

          <Stack spacing={2}>
            {editableFields.map((field) => (
              <TextField
                key={field}
                size="small"
                label={field}
                value={values[field] ?? ""}
                onChange={(e) => setValues({ ...values, [field]: e.target.value })}
                helperText={
                  values[field] === "" || values[field] === undefined
                    ? t("tax.notExtracted", "Non extrait - a saisir si necessaire")
                    : undefined
                }
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewingId(null)}>{t("common.cancel", "Annuler")}</Button>
          {/* Disabled rather than letting the call fail: the server returns 422
              when a profile-targeted document has no vehicle, and a button that
              only tells you it was unusable after you pressed it is worse than
              one that says so up front. */}
          <Button
            variant="contained"
            onClick={confirm}
            disabled={apply.isPending || (needsVehicle && !reviewVehicleId)}
          >
            {t("tax.confirmApply", "Valider et enregistrer")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* One hidden input for the whole table — the row it belongs to is held
          in replaceTargetId while the OS picker is open. */}
      <input
        ref={replaceInput} type="file" hidden
        accept="application/pdf,image/jpeg,image/png,image/webp,image/heic"
        onChange={(e) => onReplacePicked(e.target.files?.[0])}
      />

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        <MenuItem onClick={() => menuDocument && openEdit(menuDocument)}>
          <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t("tax.docEdit", "Modifier le type / le vehicule")}</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => menuDocument && startReplace(menuDocument)}
          disabled={replaceFile.isPending}
        >
          <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t("tax.docReplace", "Remplacer le fichier")}</ListItemText>
        </MenuItem>

        {/* Rejeter stays distinct from Supprimer on purpose: it closes the
            review without touching the file, which is what you want for a
            document you decided not to use but must keep. */}
        {menuDocument?.status === "NEEDS_REVIEW" && (
          <MenuItem
            onClick={() => {
              reject.mutate(menuDocument.id);
              closeMenu();
            }}
          >
            <ListItemIcon><BlockIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{t("common.reject", "Rejeter")}</ListItemText>
          </MenuItem>
        )}

        <Divider />

        <MenuItem
          onClick={() => {
            setDeleting(menuDocument);
            closeMenu();
          }}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>{t("common.delete", "Supprimer")}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Changing the type is not a relabelling: it decides which fields the
          server will accept and where they get written, so the extraction is
          thrown away and redone. Said here, before the click, not in a toast
          afterwards. */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("tax.docEditTitle", "Corriger l'import")}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {editing?.original_name ?? `#${editing?.id}`}
          </Typography>

          <Stack spacing={2}>
            <TextField
              select size="small" fullWidth
              label={t("tax.docType", "Type")}
              value={editType}
              onChange={(e) => setEditType(e.target.value)}
            >
              {DOC_TYPES.map((type) => (
                <MenuItem key={type.key} value={type.key}>{type.label}</MenuItem>
              ))}
            </TextField>

            <VehiclePicker
              fleetId={fleetId}
              value={editVehicleId}
              onChange={setEditVehicleId}
              label={t("tax.vehicleOptional", "Vehicule (optionnel)")}
            />

            {editing && editType !== editing.doc_type && (
              <Alert severity="warning">
                {t(
                  "tax.docEditTypeWarning",
                  "Changer le type relance l'analyse : les valeurs extraites precedemment sont abandonnees et le document repasse en attente de validation."
                )}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>{t("common.cancel", "Annuler")}</Button>
          <Button variant="contained" onClick={submitEdit} disabled={update.isPending}>
            {t("common.save", "Enregistrer")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleting} onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("tax.docDeleteTitle", "Supprimer ce document ?")}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText component="div">
            <Typography variant="body2" fontWeight={600} gutterBottom>
              {deleting?.original_name ?? `#${deleting?.id}`}
            </Typography>
            {/* Stating exactly what survives and what does not. "Supprimer"
                means the file is really gone from the storage; the import
                trace is kept for the audit, and a document that came from the
                coffre-fort keeps its original there. */}
            <Typography variant="body2" color="text.secondary">
              {t(
                "tax.docDeleteWarning",
                "Le fichier sera definitivement supprime du stockage. La trace de l'import (qui, quand, quel fichier) est conservee pour l'audit, et si le document provient du coffre-fort l'original y reste intact. Cette action est irreversible."
              )}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>{t("common.cancel", "Annuler")}</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={remove.isPending}>
            {remove.isPending ? t("common.loading", "Suppression...") : t("common.delete", "Supprimer")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

/**
 * Reprise des documents deja presents dans le coffre-fort.
 *
 * WHY THIS PANEL EXISTS
 * ---------------------
 * `strongbox` and `fiscal_document` are two document stores that never met.
 * Customers upload cartes grises to the coffre-fort as ordinary fleet
 * management; this module read only its own table. So a fleet with every
 * registration certificate on file opened Optimisation fiscale, saw a
 * completeness of zero, and was asked to upload the same PDFs again.
 *
 * The panel renders nothing when there is nothing to offer, so a fleet that
 * has no coffre-fort documents (or has already imported them all) never sees
 * an empty box explaining a feature it does not need.
 */
const ReprisePanel: React.FC<{ fleetId: number }> = ({ fleetId }) => {
  const { t } = useTranslation();
  const { candidates, isLoading } = useDiscoverableDocuments(fleetId);
  const importExisting = useImportExistingDocuments(fleetId);

  // null = nothing chosen yet, which means "all of them". Selecting is an
  // opt-out: the common case is wanting everything, and pre-ticking a hundred
  // rows so the user can untick two is worse than defaulting to all.
  const [selected, setSelected] = useState<number[] | null>(null);
  const [expanded, setExpanded] = useState(false);

  if (isLoading || candidates.length === 0) {
    return null;
  }

  const isSelected = (id: number) => selected === null || selected.includes(id);

  const toggle = (id: number) => {
    const current = selected ?? candidates.map((c) => c.strongbox_id);
    setSelected(
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    );
  };

  const chosen = selected ?? candidates.map((c) => c.strongbox_id);

  const run = () => {
    importExisting.mutate(
      // Sending undefined rather than the full list when nothing was unticked:
      // the server then imports everything eligible under its own cap, which
      // stays correct even if a document was added since this list was fetched.
      selected === null ? {} : { strongbox_ids: selected },
      { onSuccess: () => setSelected(null) }
    );
  };

  const report = importExisting.data;

  return (
    <Alert severity="info" icon={false} sx={{ "& .MuiAlert-message": { width: "100%" } }}>
      <AlertTitle sx={{ fontWeight: 700 }}>
        {t("tax.repriseTitle", {
          defaultValue: "{{count}} document(s) deja dans votre coffre-fort peuvent alimenter la fiscalite",
          count: candidates.length,
        })}
      </AlertTitle>

      <Typography variant="body2" sx={{ mb: 2 }}>
        {t(
          "tax.repriseHint",
          "Inutile de les reimporter : nous pouvons les reprendre tels quels. Les valeurs seront extraites "
            + "puis soumises a votre validation, exactement comme un import manuel. Rien n'est ecrit sur vos "
            + "fiches fiscales sans votre confirmation, et vos documents restent intacts dans le coffre-fort."
        )}
      </Typography>

      {report && (
        <Alert
          severity={report.failed > 0 ? "warning" : "success"}
          sx={{ mb: 2 }}
        >
          {report.message}
          {report.failed > 0 && (
            <Typography variant="caption" display="block">
              {t("tax.repriseFailed", {
                defaultValue:
                  "{{count}} document(s) n'ont pas pu etre lus (fichier introuvable ou format ancien). "
                  + "Ils restent disponibles dans le coffre-fort et peuvent etre importes manuellement.",
                count: report.failed,
              })}
            </Typography>
          )}
        </Alert>
      )}

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: expanded ? 2 : 0 }}>
        <Button
          variant="contained"
          size="small"
          onClick={run}
          disabled={importExisting.isPending || chosen.length === 0}
        >
          {importExisting.isPending
            ? t("tax.repriseRunning", "Reprise en cours...")
            : t("tax.repriseAction", {
                defaultValue: "Reprendre {{count}} document(s)",
                count: chosen.length,
              })}
        </Button>

        <Button size="small" color="inherit" onClick={() => setExpanded((value) => !value)}>
          {expanded ? t("common.hide", "Masquer le detail") : t("common.showDetail", "Voir le detail")}
        </Button>
      </Stack>

      {expanded && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>{t("tax.file", "Fichier")}</TableCell>
              <TableCell>{t("tax.docType", "Type")}</TableCell>
              <TableCell>{t("tax.vehicle", "Vehicule")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {candidates.map((candidate: DiscoverableDocument) => (
              <TableRow key={candidate.strongbox_id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={isSelected(candidate.strongbox_id)}
                    onChange={() => toggle(candidate.strongbox_id)}
                  />
                </TableCell>
                <TableCell>{candidate.name ?? `#${candidate.strongbox_id}`}</TableCell>
                <TableCell>
                  {DOC_TYPES.find((type) => type.key === candidate.doc_type)?.label ?? candidate.doc_type}
                </TableCell>
                <TableCell>
                  {/* The plate comes from the server with the candidate: this
                      list can name vehicles the fleet dropdown no longer
                      offers, and resolving it client-side would show "-". */}
                  {candidate.vehicle_plate ?? `#${candidate.vehicle_id}`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Alert>
  );
};
